/* About Us page interactions. Vanilla JS, no globals: one IIFE with a
   per section registry. Sections initialize themselves via
   [data-about-section] and clean up on shopify:section:unload. */
(function () {
  'use strict';

  if (window.__bbAboutBound) return;
  window.__bbAboutBound = true;

  var registry = new Map();
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktop = window.matchMedia('(min-width: 990px)');

  function prefersMotion(root) {
    return !reduced.matches && root.dataset.aboutAnimations !== 'false';
  }

  /* A mid session reduce motion toggle must stop everything that is not
     pure CSS: playing videos, inline parallax transforms, cinematic mode. */
  reduced.addEventListener('change', function () {
    registry.forEach(function (state) {
      state.reducedHandlers.forEach(function (fn) { fn(); });
    });
  });

  /* ---------- Generic staggered reveals ---------- */
  function initReveals(root, state) {
    var items = root.querySelectorAll('[data-about-reveal]');
    if (!prefersMotion(root)) return;
    root.classList.add('bb-about-anim');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        if (entry.target === root) {
          root.classList.add('bb-about-section-in');
        } else {
          entry.target.classList.add('bb-about-in');
        }
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    io.observe(root);
    items.forEach(function (el, i) {
      var delay = parseFloat(el.dataset.aboutDelay || '0');
      if (el.dataset.aboutStagger !== undefined) delay += Math.min(i, 3) * 0.08;
      el.style.setProperty('--bb-about-delay', delay.toFixed(2) + 's');
      io.observe(el);
    });
    state.observers.push(io);
  }

  /* ---------- Scroll progress driver (rAF only while visible) ----------
     `neutral` is reported when the element is not taller than the viewport,
     so each consumer picks a sensible resting value for that case. */
  function progressDriver(el, onProgress, state, neutral) {
    var visible = false;
    var ticking = false;
    if (neutral === undefined) neutral = 1;

    function measure() {
      ticking = false;
      var rect = el.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      var total = rect.height - vh;
      if (total <= 0) { onProgress(neutral); return; }
      var p = Math.min(1, Math.max(0, -rect.top / total));
      onProgress(p);
    }
    function requestTick() {
      if (!visible || ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }
    var io = new IntersectionObserver(function (entries) {
      visible = entries[entries.length - 1].isIntersecting;
      if (visible) requestTick();
    });
    io.observe(el);
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    state.observers.push(io);
    state.teardowns.push(function () {
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('resize', requestTick);
    });
    requestTick();
  }

  /* ---------- Hero ---------- */
  function initHero(root, state) {
    var video = root.querySelector('[data-about-video]');
    if (video) {
      var attach = function () {
        if (video.dataset.loaded) return;
        if (!desktop.matches || reduced.matches) return;
        var src = video.getAttribute('data-src');
        if (!src) return;
        video.src = src;
        video.dataset.loaded = 'true';
        video.play().catch(function () {});
      };
      attach();
      var onMq = function () {
        if (!desktop.matches || reduced.matches) {
          if (video.dataset.loaded) video.pause();
          return;
        }
        attach();
        if (video.dataset.loaded) video.play().catch(function () {});
      };
      desktop.addEventListener('change', onMq);
      state.teardowns.push(function () { desktop.removeEventListener('change', onMq); });
      state.reducedHandlers.push(onMq);
    }
    if (prefersMotion(root)) {
      root.classList.add('bb-about-anim');
      /* Intro choreography: CSS transitions keyed off this class. */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          root.classList.add('bb-about-hero--in');
        });
      });
    }
    var cta = root.querySelector('[data-about-hero-next]');
    if (cta) {
      var onClick = function (e) {
        e.preventDefault();
        var section = root.closest('.shopify-section');
        var next = section && section.nextElementSibling;
        if (next) next.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth' });
      };
      cta.addEventListener('click', onClick);
      state.teardowns.push(function () { cta.removeEventListener('click', onClick); });
    }
  }

  /* ---------- Story: gentle parallax on the media column ----------
     The desktop and reduced motion gates live inside the callback, so
     resizes and mid session preference changes are always honored. */
  function initStory(root, state) {
    initReveals(root, state);
    var media = root.querySelector('[data-about-parallax]');
    if (!media || root.dataset.aboutAnimations === 'false') return;
    var apply = function (p) {
      if (!desktop.matches || reduced.matches) {
        media.style.transform = '';
        return;
      }
      var shift = (p - 0.5) * -48; /* capped at 24px either way */
      media.style.transform = 'translateY(' + shift.toFixed(1) + 'px)';
    };
    progressDriver(root, apply, state, 0.5);
    state.reducedHandlers.push(function () { media.style.transform = ''; });
    state.teardowns.push(function () { media.style.transform = ''; });
  }

  /* ---------- Process ---------- */
  /* Deck: one step at a time, moved by the arrows. It used to be driven by
     scroll position inside a 200vh sticky wrapper; nothing here reads scroll
     any more, so the section is its own height and the page just scrolls past. */
  function initProcess(root, state) {
    initReveals(root, state);
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-about-step]'));
    if (!steps.length) return;
    var prevBtn = root.querySelector('[data-about-prev]');
    var nextBtn = root.querySelector('[data-about-next]');
    var last = steps.length - 1;
    var idx = 0; /* deck position; the stacked layout does not disturb it */

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    function paint(index) {
      steps.forEach(function (step, i) {
        step.classList.toggle('bb-about-step--active', i === index);
        step.classList.toggle('bb-about-step--past', i < index);
      });
      var current = root.querySelector('[data-about-step-current]');
      if (current) current.textContent = pad(index + 1);
      var bar = root.querySelector('[data-about-progress]');
      if (bar) bar.style.width = (((index + 1) / steps.length) * 100).toFixed(1) + '%';
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === last;
    }

    function activate(index) {
      idx = Math.max(0, Math.min(last, index));
      paint(idx);
    }

    function deckWanted() {
      return root.dataset.aboutSticky !== 'false' && prefersMotion(root) && desktop.matches;
    }
    function setMode() {
      var deck = deckWanted();
      root.classList.toggle('bb-about-process--cinematic', deck);
      /* Stacked layout already shows every step, so paint the last one and
         leave the deck index alone for when desktop comes back. */
      paint(deck ? idx : last);
    }
    setMode();
    desktop.addEventListener('change', setMode);
    state.teardowns.push(function () { desktop.removeEventListener('change', setMode); });
    state.reducedHandlers.push(setMode);

    function onClick(e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-about-prev]')) activate(idx - 1);
      else if (e.target.closest('[data-about-next]')) activate(idx + 1);
    }
    root.addEventListener('click', onClick);
    state.teardowns.push(function () { root.removeEventListener('click', onClick); });

    function onKey(e) {
      if (!root.classList.contains('bb-about-process--cinematic')) return;
      if (e.key === 'ArrowRight') { activate(idx + 1); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { activate(idx - 1); e.preventDefault(); }
    }
    root.addEventListener('keydown', onKey);
    state.teardowns.push(function () { root.removeEventListener('keydown', onKey); });

    /* Theme editor: selecting a step block shows it. */
    var onBlockSelect = function (e) {
      var step = e.target && e.target.closest ? e.target.closest('[data-about-step]') : null;
      if (step && root.contains(step)) activate(steps.indexOf(step));
    };
    document.addEventListener('shopify:block:select', onBlockSelect);
    state.teardowns.push(function () { document.removeEventListener('shopify:block:select', onBlockSelect); });
  }

  /* ---------- Section registry ---------- */
  var INITS = {
    hero: initHero,
    story: initStory,
    principles: initReveals,
    process: initProcess,
    lackpard: initReveals,
    cta: initReveals
  };

  function initSection(root) {
    var id = root.getAttribute('data-about-section');
    if (!id || registry.has(id)) return;
    var state = { observers: [], teardowns: [], reducedHandlers: [] };
    registry.set(id, state);
    var type = root.getAttribute('data-about-type');
    var init = INITS[type];
    if (init) init(root, state);
  }

  function destroySection(id) {
    var state = registry.get(id);
    if (!state) return;
    state.observers.forEach(function (o) { o.disconnect(); });
    state.teardowns.forEach(function (fn) { fn(); });
    registry.delete(id);
  }

  function scan() {
    document.querySelectorAll('[data-about-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-about-section]');
    if (root) initSection(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-about-section]');
    if (root) destroySection(root.getAttribute('data-about-section'));
  });
})();
